import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Image, Input, Spinner, Text } from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopSearch,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { getMyRelativeListApi } from "../../api/finance.collaborator.api";
import { resolveImageUrl } from "../../utils/format";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../../config";

const BORDER = "#E1E1E1";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const LIMIT = 10;
const fmtD = (v) => (v ? moment(v).format("DD/MM/YYYY") : "-");
// Khớp getGenderText app: 0 → Nam, 1 → Nữ, 2 → Khác.
const genderText = (g) => {
  if (Number(g) === 0) return "Nam";
  if (Number(g) === 1) return "Nữ";
  if (Number(g) === 2) return "Khác";
  return "-";
};

const Row = ({ label, value }) => (
  <Flex gap="12px" align="flex-start" mb="8px">
    <Text fontSize="14px" color={MUTED} flex="0 0 42%">{label}</Text>
    <Text fontSize="14px" fontWeight={600} color={TEXT} flex="1" textAlign="right" noOfLines={2}>{value || "-"}</Text>
  </Flex>
);

const RelativeCard = ({ item }) => {
  const [open, setOpen] = useState(false);
  const working = item?.status === 0;
  const avatar = item?.avatar ? resolveImageUrl(item.avatar, EMP_API_BASE_URL) : appImages.empty_avatar;
  return (
    <Box bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
      <Flex as="button" w="100%" align="center" gap="12px" onClick={() => setOpen((p) => !p)}>
        <Image src={avatar} alt="" w="40px" h="40px" borderRadius="full" objectFit="cover" flexShrink={0} fallbackSrc={appImages.empty_avatar} />
        <Box textAlign="left" flex="1" minW="0">
          <Text fontSize="14px" fontWeight={600} color={TEXT}>{item?.fullName || "-"} - {item?.code || "-"}</Text>
          <Flex align="center" gap="8px" mt="4px">
            <Text fontSize="13px" fontWeight={500} color={working ? "#0BA859" : "#E5484D"}>{working ? "Đang làm việc" : "Dừng làm việc"}</Text>
            <Text fontSize="13px" color={MUTED}>{fmtD(item?.startTime)}</Text>
          </Flex>
        </Box>
        <img src={open ? appImages.icon_chevron_down : appImages.icon_chevron_right} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
      </Flex>
      {open && (
        <>
          <Box h="1px" bg={BORDER} my="12px" />
          <Row label="Email" value={item?.email} />
          <Row label="Số điện thoại" value={item?.phone} />
          <Row label="CCCD" value={item?.identificationNumber} />
          <Row label="Ngày sinh" value={fmtD(item?.dateOfBirth)} />
          <Row label="Giới tính" value={genderText(item?.gender)} />
          <Row label="Mã số thuế" value={item?.taxCode} />
          <Row label="Địa chỉ thường trú" value={item?.permanentAddress} />
          <Row label="Địa chỉ hiện tại" value={item?.currentAddress} />
          <Row label="Khối" value={item?.saleBlock?.name} />
          <Row label="Vùng" value={item?.saleArea?.name} />
        </>
      )}
    </Box>
  );
};

const RelativeList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);
  const searchTimer = useRef(null);

  const fetchPage = useCallback(
    async (p, mode, kw) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getMyRelativeListApi({ page: p, limit: LIMIT, search: kw || undefined });
        if (rid !== reqId.current) return;
        const list = data?.items || data || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setTotal(data?.pagination?.total ?? list.length);
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    []
  );

  useEffect(() => { fetchPage(1, "replace", search); }, [search, fetchPage]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v.trim()), 300);
  };

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => ({
      id: it._id,
      avatar: it?.avatar ? resolveImageUrl(it.avatar, EMP_API_BASE_URL) : appImages.empty_avatar,
      name: `${it?.fullName || "-"} - ${it?.code || "-"}`,
      working: !it?.status,
      startTime: fmtD(it?.startTime),
      email: it?.email || "-",
      phone: it?.phone || "-",
      identification: it?.identificationNumber || "-",
      block: it?.saleBlock?.name || "-",
      area: it?.saleArea?.name || "-",
    }));

    return (
      <DesktopPage title="Quản lý CTV">
        <DesktopStatRow items={[{ key: "total", title: "Tổng số CTV", total: total || 0 }]} />

        <DesktopPanel p="0">
          <Box p="16px" w="360px">
            <Text fontSize="13px" color={TEXT} mb="6px">
              Tìm kiếm
            </Text>
            <DesktopSearch placeholder="Nhập tên CTV ..." onChange={onSearchChange} maxW="360px" />
          </Box>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có cộng tác viên"
            rows={rows}
            headers={[
              {
                key: "name",
                label: "Cộng tác viên",
                align: "left",
                width: "24%",
                render: (row) => (
                  <Flex align="center" gap="12px">
                    <Image
                      src={row.avatar}
                      alt=""
                      w="36px"
                      h="36px"
                      borderRadius="full"
                      objectFit="cover"
                      fallbackSrc={appImages.empty_avatar}
                    />
                    <Text fontSize="14px" fontWeight={600} color={TEXT}>
                      {row.name}
                    </Text>
                  </Flex>
                ),
              },
              {
                key: "working",
                label: "Trạng thái",
                width: "12%",
                render: (row) => (
                  <Text fontSize="14px" color={row.working ? "#0BA859" : "#E5484D"}>
                    {row.working ? "Đang làm việc" : "Dừng làm việc"}
                  </Text>
                ),
              },
              { key: "startTime", label: "Ngày vào làm", width: "11%" },
              { key: "phone", label: "Số điện thoại", width: "12%" },
              { key: "email", label: "Email", align: "left", width: "15%" },
              { key: "identification", label: "CCCD", width: "12%" },
              { key: "block", label: "Khối", align: "left", width: "7%" },
              { key: "area", label: "Vùng", align: "left", width: "7%" },
            ]}
          />

          {hasNext && (
            <Flex
              as="button"
              onClick={() => fetchPage(page + 1, "append", search)}
              justify="center"
              py="16px"
              w="100%"
              color={APP.primary200}
              fontWeight={600}
              fontSize="14px"
            >
              {loading ? <Spinner size="sm" /> : "Xem thêm"}
            </Flex>
          )}
        </DesktopPanel>
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Quản lý CTV" onBack={() => navigate(`${EMP_BASE_PATH}/tai-chinh/cong-tac-vien`)} />
      <Box px="16px" pt="12px">
        <Flex h="44px" align="center" px="12px" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px">
          <AppIcon src={appImages.icon_search} size={20} color="#94A3B8" />
          <Input variant="unstyled" ml="8px" fontSize="14px" placeholder="Nhập tên CTV ..." onChange={onSearchChange} />
        </Flex>

        <Box mt="12px" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" p="14px">
          <Text fontSize="13px" color={MUTED} fontWeight={500}>TỔNG SỐ CTV</Text>
          <Text fontSize="22px" fontWeight={800} color={APP.primary200} mt="8px">{total || 0}</Text>
        </Box>

        <Text fontSize="14px" fontWeight={600} color={TEXT} mt="16px" mb="4px">Danh sách CTV</Text>
        <Box h="10px" />
        {loading && !items.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">Không có cộng tác viên</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => <RelativeCard key={it._id} item={it} />)}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append", search)} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default RelativeList;
